/**
 * Customer detail — one borrower's whole relationship in one place:
 * profile, loans and schedules, payments, applications, KYC, contact
 * history, and the audit trail for the record itself.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Phone } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatCurrency } from "@/lib/lendingCalculations";
import { staffApi } from "@/platform/staffApi";

type Row = Record<string, any>;

const tone: Record<string, string> = {
  approved: "bg-success/10 text-success",
  active: "bg-success/10 text-success",
  paid: "bg-success/10 text-success",
  paid_off: "bg-muted text-muted-foreground",
  kept: "bg-success/10 text-success",
  pending: "bg-warning/10 text-warning",
  partial: "bg-warning/10 text-warning",
  late: "bg-warning/10 text-warning",
  rejected: "bg-destructive/10 text-destructive",
  broken: "bg-destructive/10 text-destructive",
  suspended: "bg-destructive/10 text-destructive",
  default: "bg-destructive/10 text-destructive",
};
const badge = (v?: string | null) =>
  <Badge className={tone[v || ""] || "bg-muted text-muted-foreground"}>{v || "—"}</Badge>;

export function CustomerDetailPage() {
  const { customerId } = useParams();
  const [data, setData] = useState<Row | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId) return;
    staffApi.customerDetail(customerId)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load the customer"))
      .finally(() => setLoading(false));
  }, [customerId]);

  if (loading) return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin" />;
  if (error || !data) {
    return (
      <div className="py-16 text-center">
        <p className="text-sm text-destructive">{error || "Not found"}</p>
        <Button variant="link" asChild className="mt-2"><Link to="/staff/clients">Back to Clients</Link></Button>
      </div>
    );
  }

  const p = data.profile, s = data.summary;

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild className="-ml-2">
        <Link to="/staff/clients"><ArrowLeft className="mr-1 h-4 w-4" />Clients</Link>
      </Button>

      {/* Identity */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold">{p.full_name || "Unnamed customer"}</h1>
              <a href={`tel:${p.phone}`} className="mt-1 flex items-center gap-2 text-sm text-primary">
                <Phone className="h-4 w-4" />{p.phone}
              </a>
              <p className="mt-1 text-xs text-muted-foreground">
                NRC {p.nrc_number || "—"} · joined{" "}
                {p.joined ? new Date(p.joined).toLocaleDateString() : "—"} · {p.plan_tier} plan
              </p>
            </div>
            <div className="flex gap-2">
              {badge(p.kyc_status)}
              {badge(p.status)}
            </div>
          </div>

          <div className="mt-5 grid gap-4 border-t pt-4 sm:grid-cols-4">
            <div>
              <p className="text-xs text-muted-foreground">Outstanding</p>
              <p className="text-lg font-semibold tabular-nums">{formatCurrency(s.outstanding)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Borrowed to date</p>
              <p className="text-lg font-semibold tabular-nums">{formatCurrency(s.total_borrowed)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Repaid to date</p>
              <p className="text-lg font-semibold tabular-nums">{formatCurrency(s.total_repaid)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Promises</p>
              <p className="text-lg font-semibold tabular-nums">
                <span className="text-success">{s.kept_promises}</span>
                {" / "}
                <span className={s.broken_promises > 0 ? "text-destructive" : ""}>
                  {s.broken_promises}
                </span>
              </p>
              <p className="text-xs text-muted-foreground">kept / broken</p>
            </div>
          </div>
          {s.open_cases > 0 && (
            <p className="mt-3 text-sm text-warning">
              {s.open_cases} open collection case —{" "}
              <Link to="/staff/collections" className="underline">go to Collections</Link>
            </p>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="loans">
        <TabsList>
          <TabsTrigger value="loans">Loans ({data.loans.length})</TabsTrigger>
          <TabsTrigger value="payments">Payments ({data.payments.length})</TabsTrigger>
          <TabsTrigger value="applications">Applications ({data.applications.length})</TabsTrigger>
          <TabsTrigger value="kyc">KYC ({data.kyc.length})</TabsTrigger>
          <TabsTrigger value="contact">Contact ({data.interactions.length})</TabsTrigger>
          <TabsTrigger value="messages">
            Messages ({(data.notifications || []).length})
          </TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="loans" className="space-y-4 pt-4">
          {data.loans.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
              No loans yet.
            </CardContent></Card>
          ) : data.loans.map((l: Row) => (
            <Card key={l.id}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-base">
                  <Link to={`/staff/loans/${l.id}`} className="text-primary hover:underline">
                    {formatCurrency(l.principal)} · {l.term_months} months
                  </Link>
                  {badge(l.status)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 text-sm sm:grid-cols-4">
                  <div><p className="text-xs text-muted-foreground">Outstanding</p>
                    <p className="font-medium tabular-nums">{formatCurrency(l.outstanding)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Monthly</p>
                    <p className="font-medium tabular-nums">{formatCurrency(l.monthly_payment)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Rate</p>
                    <p className="font-medium tabular-nums">{l.interest_rate}% p.a.</p></div>
                  <div><p className="text-xs text-muted-foreground">Disbursed</p>
                    <p className="font-medium">{l.disbursed_on
                      ? new Date(l.disbursed_on).toLocaleDateString() : "not yet"}</p></div>
                </div>
                {l.installments.length > 0 && (
                  <div className="mt-4 space-y-1">
                    {l.installments.map((i: Row) => (
                      <div key={i.sequence}
                        className="flex items-center justify-between rounded border px-3 py-1.5 text-sm">
                        <span>#{i.sequence} · {i.due_date
                          ? new Date(i.due_date).toLocaleDateString() : "—"}</span>
                        <span className="tabular-nums">
                          {formatCurrency(i.amount_paid)} / {formatCurrency(i.amount_due)}
                        </span>
                        {badge(i.status)}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="payments" className="pt-4">
          <Card><CardContent className="pt-4">
            {data.payments.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No payments received.</p>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead>
                  <TableHead>Method</TableHead><TableHead>Reference</TableHead><TableHead>Loan</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.payments.map((r: Row) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm">
                        {r.date ? new Date(r.date).toLocaleDateString() : "—"}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatCurrency(r.amount)}</TableCell>
                      <TableCell className="text-sm capitalize">
                        {String(r.method || "—").split("_").join(" ")}</TableCell>
                      <TableCell className="font-mono text-xs">{r.reference || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{r.loan_ref}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="applications" className="pt-4">
          <Card><CardContent className="pt-4">
            {data.applications.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No applications.</p>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Applied</TableHead><TableHead className="text-right">Amount</TableHead>
                  <TableHead>Term</TableHead><TableHead>Purpose</TableHead>
                  <TableHead>Status</TableHead><TableHead>Decision</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.applications.map((a: Row) => (
                    <TableRow key={a.id}>
                      <TableCell className="text-sm">{a.applied_on
                        ? new Date(a.applied_on).toLocaleDateString() : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(a.amount)}</TableCell>
                      <TableCell>{a.term_months} mo</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{a.purpose || "—"}</TableCell>
                      <TableCell>{badge(a.status)}</TableCell>
                      <TableCell className="max-w-[16rem] text-xs text-muted-foreground">
                        {a.decision_reason || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="kyc" className="pt-4">
          <Card><CardContent className="pt-4">
            {data.kyc.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No KYC submitted.</p>
            ) : data.kyc.map((k: Row) => (
              <div key={k.id} className="flex items-center justify-between border-b py-3 last:border-0">
                <div>
                  <p className="text-sm font-medium capitalize">
                    {String(k.document_type).split("_").join(" ")}</p>
                  <p className="text-xs text-muted-foreground">
                    {k.submitted_on ? new Date(k.submitted_on).toLocaleString() : "—"} · {k.files} file(s)
                  </p>
                  {k.notes && <p className="mt-1 text-xs text-muted-foreground">{k.notes}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {Array.from({ length: k.files }).map((_, i) => (
                    <Button key={i} size="sm" variant="link" className="h-auto px-1"
                      onClick={() => window.open(staffApi.kycFileUrl(k.id, i), "_blank")}>
                      file {i + 1}
                    </Button>
                  ))}
                  {badge(k.status)}
                </div>
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="contact" className="pt-4">
          <Card><CardContent className="pt-4">
            {data.interactions.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No contact logged with this customer.
              </p>
            ) : (
              <ol className="space-y-3 border-l pl-4">
                {data.interactions.map((i: Row, idx: number) => (
                  <li key={idx} className="relative text-sm">
                    <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary" />
                    <p className="text-xs text-muted-foreground">
                      {new Date(i.at).toLocaleString()}</p>
                    <p className="font-medium">
                      {String(i.channel).split("_").join(" ")} — {String(i.outcome).split("_").join(" ")}
                    </p>
                    {i.notes && <p className="text-muted-foreground">{i.notes}</p>}
                  </li>
                ))}
              </ol>
            )}
            {data.promises.length > 0 && (
              <div className="mt-5 border-t pt-4">
                <p className="mb-2 text-sm font-medium">Promises</p>
                {data.promises.map((pr: Row, i: number) => (
                  <div key={i} className="flex items-center justify-between py-1 text-sm">
                    <span>{formatCurrency(pr.amount)} by {new Date(pr.date).toLocaleDateString()}</span>
                    {badge(pr.status)}
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* What the borrower was told, in their words not ours — the answer to
            "I was never informed". */}
        <TabsContent value="messages" className="pt-4">
          <Card><CardContent className="pt-4">
            {(data.notifications || []).length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nothing has been sent to this customer yet.
              </p>
            ) : (
              <div className="space-y-3">
                {data.notifications.map((n: Row, i: number) => (
                  <div key={i} className="rounded-md border p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium">{n.title}</p>
                      <div className="flex shrink-0 items-center gap-2">
                        <Badge className="bg-muted text-muted-foreground">
                          {n.category}
                        </Badge>
                        <Badge className={n.read
                          ? "bg-success/10 text-success"
                          : "bg-muted text-muted-foreground"}>
                          {n.read ? "read" : "unread"}
                        </Badge>
                        {n.dismissed && (
                          <Badge className="bg-muted text-muted-foreground">
                            dismissed
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{n.body}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {n.at ? new Date(n.at).toLocaleString() : "—"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="activity" className="pt-4">
          <Card><CardContent className="pt-4">
            {data.activity.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No recorded activity.</p>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>When</TableHead><TableHead>Action</TableHead><TableHead>By</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.activity.map((e: Row, i: number) => (
                    <TableRow key={i}>
                      <TableCell className="whitespace-nowrap text-xs">
                        {new Date(e.at).toLocaleString()}</TableCell>
                      <TableCell className="text-sm">
                        {String(e.action).split("_").join(" ")}</TableCell>
                      <TableCell className="text-xs capitalize text-muted-foreground">
                        {String(e.actor_role || "system").split("_").join(" ")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
